import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar from "@/pages/category/components/FilterBar";
import Pagination from "@/pages/category/components/Pagination";
import { filterMovies, movies, type SortValue } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlGenre = searchParams.get("genre") ?? "全部";
  const [genre, setGenre] = useState(urlGenre);
  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sort, setSort] = useState<SortValue>("release");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setGenre(urlGenre);
    setPage(1);
  }, [urlGenre]);

  const result = useMemo(() => filterMovies({ genre, year, region, sort }), [genre, year, region, sort]);
  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const visible = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  usePageMeta({
    title:
      genre === "全部"
        ? "影视剧分类 - “奸情”影视剧推荐"
        : `${genre}剧 - 影视剧分类 - “奸情”影视剧推荐`,
    description:
      genre === "全部"
        ? `按类型、年份、地区筛选影视剧，共收录 ${movies.length} 部精选剧集，支持按上映时间、评分与热度排序。`
        : `汇集${genre}题材影视剧，可按年份与地区进一步筛选，当前共 ${result.length} 部作品，附剧情简介与深度解析。`,
    keywords: `影视剧分类,${genre}剧,影视推荐,剧情解析,奸情影视`,
  });

  const updateGenre = (value: string) => {
    setGenre(value);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value === "全部") {
      next.delete("genre");
    } else {
      next.set("genre", value);
    }
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setYear("全部");
    setRegion("全部");
    setSort("release");
    setPage(1);
    updateGenre("全部");
  };

  const handleFilterChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-24 md:px-8 md:pb-14 md:pt-32 lg:px-12">
          <div className="w-full">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视剧分类" }]} />
            <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <h1 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
                  {genre === "全部" ? "影视剧分类" : `${genre}剧分类`}
                </h1>
                <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-[15px]">
                  按类型、年份与地区组合筛选，找到此刻最想看的剧。每部作品都配有剧情简介与分章节的深度解析，
                  点击卡片即可进入详情页阅读。
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5">
                <i className="ri-film-line text-base text-accent-600" aria-hidden="true" />
                <span className="text-xs text-foreground-500">
                  当前收录 <span className="font-label font-bold text-primary-800">{result.length}</span> 部
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-12 lg:px-12">
          <FilterBar
            genre={genre}
            year={year}
            region={region}
            sort={sort}
            resultCount={result.length}
            onGenreChange={updateGenre}
            onYearChange={handleFilterChange(setYear)}
            onRegionChange={handleFilterChange(setRegion)}
            onSortChange={(value) => {
              setSort(value);
              setPage(1);
            }}
            onReset={resetFilters}
          />

          {visible.length > 0 ? (
            <>
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                {visible.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-2xl text-foreground-400">
                <i className="ri-inbox-archive-line" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到符合条件的影视剧</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-foreground-500">
                当前筛选组合（{genre} / {year} / {region}）暂时没有匹配内容。可以换个类型，或者重置筛选条件再试一次。
              </p>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-900 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800"
                >
                  <i className="ri-refresh-line text-base" aria-hidden="true" />
                  重置全部筛选
                </button>
                <Link
                  to="/search"
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700"
                >
                  直接搜索剧名
                  <i className="ri-search-line text-base" aria-hidden="true" />
                </Link>
              </div>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}